import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { errorText } from './dmControlUtils';
import { useFlash } from './useFlash';

/** Every write of the control shares this key, so one can wait for another. */
export function dmControlMutationKey(patientId: string) {
    return ['dm-control', patientId];
}

/** True while any DM control write for the patient runs, whichever part of the modal started it. */
export function useDmControlBusy(patientId: string): boolean {
    return useIsMutating({ mutationKey: dmControlMutationKey(patientId) }) > 0;
}

// Every DM write refreshes the patient's queries: the live DM view, the mirrored card and
// the excluded teeth all live under ['v2/patients', patientId]. `saved` flashes after success.
export function useDmControlMutation<TVariables = void>(
    patientId: string,
    mutationFn: (variables: TVariables) => Promise<unknown>,
    onDone?: () => void
) {
    const queryClient = useQueryClient();
    const [saved, setSaved] = useFlash<true>();
    const mutation = useMutation({
        mutationKey: dmControlMutationKey(patientId),
        mutationFn,
        onMutate: () => setSaved(null),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['v2/patients', patientId] });
            setSaved(true);
            onDone?.();
        }
    });
    return {
        run: mutation.mutate,
        reset: mutation.reset,
        saving: mutation.isPending,
        saved: saved === true,
        error: mutation.isError ? errorText(mutation.error) : null
    };
}
